import { useParams } from 'react-router-dom'

import {
  ChevronDownIcon,
  Collapsible,
  EmptyState,
  Flex,
} from '@buffer-mono/popcorn'

import { ScheduleTableCell } from './ScheduleTableCell'
import { ScheduleTableHeader } from './ScheduleTableHeader'
import { usePostingSchedule } from '../../hooks/usePostingSchedule'

import styles from './ScheduleTable.module.css'

import type { JSX } from 'react'

const ScheduleTable = (): JSX.Element => {
  const { id: channelId } = useParams<{
    id: string
  }>()
  const {
    schedule,
    isEmpty,
    togglePause,
    removePostingTime,
    updatePostingTime,
    hasEditingPrivilege,
  } = usePostingSchedule(channelId ?? '')

  const disabled = !hasEditingPrivilege

  if (isEmpty) {
    return (
      <Flex direction="column" className={styles.tableWrapper}>
        <EmptyState
          className={styles.emptyState}
          data-testid="posting-schedule-empty-state"
        >
          <EmptyState.Description>
            You don&apos;t have any posting times set!
            <br />
            Add a new posting time to start publishing posts from your queue.
          </EmptyState.Description>
        </EmptyState>
      </Flex>
    )
  }

  return (
    <Flex direction="column" className={styles.tableWrapper}>
      {/* Mobile: Vertical stacked view */}
      <Flex direction="column" className={styles.mobileView}>
        {schedule.map(({ day, times, paused }, index) => {
          const hasTimes = times.length > 0
          const disabledColumn = paused || disabled
          const isFirstDayWithTimes =
            hasTimes &&
            schedule
              .slice(0, index)
              .every(({ times: prevTimes }) => prevTimes.length === 0)

          const headerContent = (
            <ScheduleTableHeader
              day={day}
              paused={paused}
              onTogglePause={(): Promise<void> => togglePause(day)}
              disabled={disabled}
              isDayEmpty={!hasTimes}
              variant="mobile"
            />
          )

          // Days with times: render collapsible with chevron
          if (hasTimes) {
            return (
              <Collapsible
                key={day}
                defaultOpen={isFirstDayWithTimes}
                className={`${styles.mobileDay} ${
                  disabledColumn ? styles.disabledColumn : ''
                }`}
                data-disabled={disabledColumn}
              >
                <Collapsible.Trigger asChild>
                  <Flex
                    direction="row"
                    justify="between"
                    align="center"
                    className={styles.mobileDayHeader}
                  >
                    {headerContent}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={styles.chevronIcon}
                    />
                  </Flex>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Flex
                    align="center"
                    direction="column"
                    className={styles.mobileColumnContent}
                  >
                    {times.map((time) => (
                      <ScheduleTableCell
                        key={`cell-${day}-${time}`}
                        day={day}
                        time={{
                          hours: parseInt(time.split(':')[0], 10),
                          minutes: parseInt(time.split(':')[1], 10),
                        }}
                        disabled={disabled}
                        onRemoveTimeClick={(): Promise<void> =>
                          removePostingTime(day, {
                            hours: parseInt(time.split(':')[0], 10),
                            minutes: parseInt(time.split(':')[1], 10),
                          })
                        }
                        onUpdateTime={(newTime): Promise<void> =>
                          updatePostingTime(
                            day,
                            {
                              hours: parseInt(time.split(':')[0], 10),
                              minutes: parseInt(time.split(':')[1], 10),
                            },
                            {
                              hours: newTime.hours,
                              minutes: newTime.minutes,
                            },
                          )
                        }
                      />
                    ))}
                  </Flex>
                </Collapsible.Content>
              </Collapsible>
            )
          }

          // Days without times: render plain header without chevron
          return (
            <Flex
              key={day}
              className={`${styles.mobileDay} ${
                disabledColumn ? styles.disabledColumn : ''
              }`}
              data-disabled={disabledColumn}
            >
              <Flex className={styles.mobileDayHeader}>{headerContent}</Flex>
            </Flex>
          )
        })}
      </Flex>

      {/* Desktop: Grid view */}
      <Flex className={`${styles.table} ${styles.desktopView}`} align="stretch">
        {schedule.map(({ day, times, paused }) => {
          const hasTimes = times.length > 0

          const disabledColumn = paused || disabled

          return (
            <Flex
              align="stretch"
              data-testid="posting-schedule-column"
              key={day}
              data-disabled={disabledColumn}
              className={styles.column}
            >
              <Flex
                className={`${styles.column} ${
                  disabledColumn ? styles.disabledColumn : ''
                }`}
                direction="column"
                align="center"
              >
                <ScheduleTableHeader
                  day={day}
                  paused={paused}
                  onTogglePause={(): Promise<void> => togglePause(day)}
                  disabled={disabled}
                  isDayEmpty={!hasTimes}
                />
                <Flex
                  align="center"
                  direction="column"
                  className={styles.columnContent}
                  data-testid="posting-schedule-column-content"
                >
                  {times.map((time) => (
                    <ScheduleTableCell
                      key={`cell-${day}-${time}`}
                      day={day}
                      time={{
                        hours: parseInt(time.split(':')[0], 10),
                        minutes: parseInt(time.split(':')[1], 10),
                      }}
                      disabled={disabled}
                      onRemoveTimeClick={(): Promise<void> =>
                        removePostingTime(day, {
                          hours: parseInt(time.split(':')[0], 10),
                          minutes: parseInt(time.split(':')[1], 10),
                        })
                      }
                      onUpdateTime={(newTime): Promise<void> =>
                        updatePostingTime(
                          day,
                          {
                            hours: parseInt(time.split(':')[0], 10),
                            minutes: parseInt(time.split(':')[1], 10),
                          },
                          {
                            hours: newTime.hours,
                            minutes: newTime.minutes,
                          },
                        )
                      }
                    />
                  ))}
                </Flex>
              </Flex>
            </Flex>
          )
        })}
      </Flex>
    </Flex>
  )
}

export { ScheduleTable }
